// src/encounter/lib/nursing-plan.ts
// [PROD-2949] The CONTENT of the nurse's Nursing plan block, as pure data. The nursing sibling of the
// allied-health plan (./allied-health-plan.ts): same section shape, same order-affordance check, same
// view building block (`PlanSectionBlock`), so one component family renders both plans.
//
// WHAT A NURSE MAY PLAN (encounter authority §2): a nursing care plan = nursing problems, goals and
// interventions (monitoring, education), kept separate from the medical plan. A nurse never orders and
// never enters a medical diagnosis. So:
//   • the structure is the NURSING PROCESS — assessment, problem, goals, interventions, evaluation —
//     the profession's own steps, not invented vocabulary;
//   • the "problem" is the nursing problem IN PLAIN WORDS. No NANDA-I or any other licensed
//     terminology, and no code field: it is not a diagnosis and must not look like one;
//   • there is no order, prescription or medication field, and `hasOrderAffordance` (shared with the
//     allied plan) proves it in the tests.
//
// Pure: no React, no fetch, no DS import.
import type { AlliedPlanSectionBase } from './allied-health-plan';

/** The framework key the server stores for a nursing care plan (NonPrescriberPlanVocabulary). */
export const NURSING_PLAN_FRAMEWORK = 'nursing-process' as const;
export type NursingPlanFramework = typeof NURSING_PLAN_FRAMEWORK;

/** The nursing plan's one section. Same shape as an allied plan section, its own discriminant. */
export interface NursingPlanSection extends AlliedPlanSectionBase {
  framework: NursingPlanFramework;
}

/**
 * The nursing process, one field per step, plus the education the authority names and the review.
 * Field ids are the server's entry keys (lower-case, hyphenated) — renaming one orphans saved text.
 */
export const NURSING_PLAN_SECTION: NursingPlanSection = {
  id: 'nursing-plan-process',
  framework: NURSING_PLAN_FRAMEWORK,
  // ONE TITLE (design desk, 5 Oct; the #609 one-title rule): the frame-v2 section around this card is
  // already titled "Nursing plan", so the card names the plan's own state instead of repeating it.
  title: "Today's plan",
  subtitle: 'Nursing process. Kept separate from the medical plan.',
  fields: [
    {
      id: 'nursing-assessment-summary',
      label: 'Assessment summary',
      hint: 'What you found in this visit, in brief.',
      multiline: true,
    },
    {
      id: 'nursing-problem',
      label: 'Nursing problem',
      hint: 'The problem in plain words. This is not a medical diagnosis.',
      multiline: true,
    },
    {
      id: 'nursing-goals',
      label: 'Goals',
      hint: 'What the patient should achieve, and by when.',
      multiline: true,
    },
    {
      id: 'nursing-interventions',
      label: 'Interventions',
      hint: 'The nursing care and monitoring you will give.',
      multiline: true,
    },
    {
      id: 'nursing-education',
      label: 'Education',
      hint: 'What you taught the patient or carer.',
      multiline: true,
    },
    {
      id: 'nursing-evaluation',
      label: 'Evaluation',
      hint: 'How the patient responded, and progress against the goals.',
      multiline: true,
    },
    {
      id: 'nursing-review',
      label: 'Review',
      hint: 'When this plan is to be checked again.',
    },
  ],
};
